/**
 * aminos-mens-sticky-cta.js
 *
 * Drives sections/aminos-mens-sticky-cta.liquid, rendered only by
 * templates/page.mens.json (/pages/mens). Page-scoped fork of
 * assets/aminos-first-offer-sticky-cta.js.
 *
 * The button is a real <a href> in the markup, so the jump works without this
 * file. What the script adds is the bar's visibility behaviour, the measured
 * clearance that stops the bar covering page content, the Shopify preview-bar
 * offset, and smooth scrolling with correct focus handling.
 *
 * The bar is bottom-docked on mobile and top-docked from 750px up (see the
 * stylesheet). Mobile clearance is padding-bottom; desktop uses scroll-padding
 * so showing the bar cannot shove the buy box back into view.
 *
 * Copied from the aminos-nutrition-support-* set (/pages/nutrition-support):
 * same code, mens copy and images.
 */
(function (global) {
  'use strict';

  if (global.__aminosMenStickyCta) return;
  global.__aminosMenStickyCta = true;

  var DESKTOP_QUERY = '(min-width: 750px)';

  /* --- Shopify admin preview bar ------------------------------------------
     Mirrors the shared sticky cart's handling. The preview bar is injected
     asynchronously and can resize, so this is polled, observed and re-checked
     on resize rather than read once. Only affects the bottom-docked layout. */
  function updatePreviewOffset() {
    var wrapper = document.getElementById('PBarNextFrameWrapper');
    var height = wrapper ? Math.ceil(wrapper.getBoundingClientRect().height || 0) : 0;
    document.documentElement.style.setProperty('--aa-sticky-preview-offset', height > 0 ? height + 'px' : '0px');
  }

  function watchPreviewBar() {
    updatePreviewOffset();
    global.setTimeout(updatePreviewOffset, 300);
    global.setTimeout(updatePreviewOffset, 1000);

    new MutationObserver(updatePreviewOffset).observe(document.documentElement, { childList: true, subtree: true });

    if ('ResizeObserver' in global) {
      var resizeObserver = new ResizeObserver(updatePreviewOffset);
      var attach = function () {
        var wrapper = document.getElementById('PBarNextFrameWrapper');
        if (wrapper) resizeObserver.observe(wrapper);
      };
      attach();
      global.setTimeout(attach, 500);
    }

    global.addEventListener('resize', updatePreviewOffset, { passive: true });
  }

  /* --- scrolling ----------------------------------------------------------- */

  function jumpTo(selector) {
    var target = document.querySelector(selector);
    if (!target) {
      console.warn('[aminos-mens-sticky-cta] No element matches "' + selector + '"');
      return false;
    }

    var reduced = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

    // The buy box anchor carries aria-hidden, so focusing it would strand a
    // screen reader on hidden content. Focus the section instead — the standard
    // skip-link pattern. tabindex -1 makes it programmatically focusable without
    // entering the tab order; preventScroll keeps focus from snapping past the
    // smooth scroll that just started.
    var section = (target.closest && target.closest('[data-aminos-fp-section]')) || target;
    if (!section.hasAttribute('tabindex')) section.setAttribute('tabindex', '-1');
    section.focus({ preventScroll: true });
    return true;
  }

  /* --- one bar ------------------------------------------------------------- */

  function isDesktop() {
    return !!(global.matchMedia && global.matchMedia(DESKTOP_QUERY).matches);
  }

  // Wider than the bar itself so a layout nudge (padding, scroll anchoring,
  // accordion open) cannot push a just-exited buy box back across the edge
  // and flip is-visible in a loop.
  function edgeBuffer(root) {
    return Math.max(120, Math.round(root.offsetHeight || 0) + 24);
  }

  function elementIsOnScreen(el, barVisible, root) {
    var rect = el.getBoundingClientRect();
    var vh = global.innerHeight || document.documentElement.clientHeight || 0;
    var buffer = edgeBuffer(root);

    if (barVisible) {
      // Bar is up: hide it only once the buy box has come well into view.
      return rect.top < vh - buffer && rect.bottom > buffer;
    }
    // Bar is down: keep treating the buy box as on-screen until it has left
    // by the same buffer, so we do not bounce at the hairline.
    return rect.bottom > -buffer && rect.top < vh + buffer;
  }

  function init(root) {
    if (!root || root.dataset.aminosMnsStickyReady === 'true') return;
    root.dataset.aminosMnsStickyReady = 'true';

    var targetSelector = root.dataset.targetSelector || '#buybox';
    var link = root.querySelector('[data-aminos-mns-sticky-link]');
    var triggerElement = document.querySelector(root.dataset.triggerSelector || '.aminos-hero');
    var hideAtTarget = root.dataset.hideAtTarget === 'true';
    var observed = null;
    var scheduled = 0;

    if (hideAtTarget) {
      var anchor = document.querySelector(targetSelector);
      observed = anchor && anchor.closest ? anchor.closest('[data-aminos-fp-section]') || anchor : anchor;
    }

    function writeHeight() {
      var gap = isDesktop() ? 0 : Number.parseFloat(global.getComputedStyle(root).bottom) || 0;
      document.documentElement.style.setProperty('--aminos-mns-sticky-height', root.offsetHeight + gap + 8 + 'px');
    }

    function setVisible(visible) {
      if (root.classList.contains('is-visible') === visible) return;

      root.classList.toggle('is-visible', visible);
      document.documentElement.classList.toggle('aminos-mns-sticky-visible', visible);
      if (visible) writeHeight();
    }

    function modeAllowsVisibility(barVisible) {
      var showMode = root.dataset.showMode || 'always';
      var slop = edgeBuffer(root);

      if (showMode === 'after_scroll') {
        var trigger = Number(root.dataset.scrollTrigger) || 0;
        return global.scrollY >= (barVisible ? Math.max(0, trigger - slop) : trigger);
      }

      if (showMode === 'after_element') {
        if (!triggerElement) return false;
        var bottom = triggerElement.getBoundingClientRect().bottom;
        return barVisible ? bottom <= slop : bottom <= 0;
      }

      return true;
    }

    function refreshVisibility() {
      var barVisible = root.classList.contains('is-visible');
      var modeAllows = modeAllowsVisibility(barVisible);
      var targetBlocks = hideAtTarget && observed && elementIsOnScreen(observed, barVisible, root);
      setVisible(modeAllows && !targetBlocks);
    }

    function requestRefresh() {
      if (scheduled) return;
      scheduled = global.requestAnimationFrame(function () {
        scheduled = 0;
        refreshVisibility();
      });
    }

    if (link) {
      link.addEventListener('click', function (event) {
        // Only take over when the target actually resolves; otherwise let the
        // browser follow the href, which is the same destination.
        if (jumpTo(targetSelector)) event.preventDefault();
      });
    }

    global.addEventListener('scroll', requestRefresh, { passive: true });
    global.addEventListener('resize', requestRefresh, { passive: true });

    refreshVisibility();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-aminos-mns-sticky]').forEach(init);
  }

  function start() {
    watchPreviewBar();
    initAll(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})(window);
